import { useMutation, useQuery, useQueries } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { QueryServerKeys } from 'constants/QueryServerKeys';
import { displaySuccessMessage, displayErrorMessage } from 'utils';
import { getErrorMessage } from 'utils/errors';
import { queryClient } from '../queryClient';
import { InstancePlatform } from 'types/cluster';
import { Platform } from 'types/platformAction';
import {
  fetchPlatformActions,
  updatePlatformAction,
  fetchPlatforms,
  fetchPlatformActionsByCode,
  fetchRoleCapabilities,
  updatePlatformActionMapping,
  updateRolePlatform,
  updateRoleAction,
} from 'services/platform';

const PLATFORM_QUERIES_STALE_TIME = 15 * 60 * 1000;
const PLATFORM_QUERIES_CACHE_TIME = 15 * 60 * 1000;

const PLATFORM_GET_QUERY_OPTIONS = {
  staleTime: PLATFORM_QUERIES_STALE_TIME,
  gcTime: PLATFORM_QUERIES_CACHE_TIME,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

export function useGetPlatformActions() {
  return useQuery({
    queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.PLATFORM_ACTIONS],
    queryFn: async () => {
      const result = await fetchPlatformActions();
      return result?.data;
    },
    ...(PLATFORM_GET_QUERY_OPTIONS),
  });
}

export function useUpdatePlatformAction() {
  const queryOptions = useMutation({
    mutationFn: async ({
      action,
      platform,
      status,
      actionDisplayName,
    }: {
      action: string;
      platform: InstancePlatform;
      status: boolean;
      actionDisplayName: string;
    }) => {
      return await updatePlatformAction({ action, platform, status });
    },
    mutationKey: [QueryServerKeys.SYSTEM_MANAGEMENT.UPDATE_PLATFORM_ACTION],
      onSuccess(result, { actionDisplayName }) {
        queryClient.invalidateQueries(
          { queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.PLATFORM_ACTIONS] },
        );
        queryClient.invalidateQueries({ queryKey: [QueryServerKeys.USER.GET_USER_DATA] });
        displaySuccessMessage(
          result.status.toString().startsWith('2')
            ? `${actionDisplayName} is successfully updated`
            : result.data?.message,
        );
      },
      onError(error: AxiosError) {
        const errorMessage = error && getErrorMessage(error);
        if (errorMessage) {
          displayErrorMessage(errorMessage);
        }
      },
  });

  return queryOptions;
}

// ── New platform-centric hooks ──────────────────────────────────────────

export function useGetPlatforms() {
  return useQuery({
    queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.PLATFORMS_LIST],
    queryFn: async () => {
      const result = await fetchPlatforms();
      return result?.data?.platforms;
    },
    ...(PLATFORM_GET_QUERY_OPTIONS),
  });
}

export function useGetPlatformActionsByCode(
  platforms: Platform[] | undefined,
) {
  return useQueries({
    queries: (platforms ?? []).map((p) => ({
      queryKey: [
        QueryServerKeys.SYSTEM_MANAGEMENT.PLATFORM_ACTIONS_BY_CODE,
        p.code,
      ],
      queryFn: async () => {
        const result = await fetchPlatformActionsByCode(p.code);
        return result?.data;
      },
      enabled: !!p.code,
      ...PLATFORM_GET_QUERY_OPTIONS,
    })),
  });
}

export function useGetRoleCapabilities(roleID: number | null) {
  return useQuery({
    queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.ROLE_CAPABILITIES, roleID],
    queryFn: async () => {
      const result = await fetchRoleCapabilities(roleID);
      return result?.data;
    },
    enabled: !!roleID,
      ...PLATFORM_GET_QUERY_OPTIONS,
  });
}

export function useGetAllRoleCapabilities(
  roles: Array<{ id: number; name: string }> | undefined,
) {
  return useQueries({
    queries: (roles ?? []).map((role) => ({
      queryKey: [
        QueryServerKeys.SYSTEM_MANAGEMENT.ROLE_CAPABILITIES,
        role.id,
      ],
      queryFn: async () => {
        const result = await fetchRoleCapabilities(role.id);
        return result?.data;
      },
      enabled: !!role.id,
      ...PLATFORM_GET_QUERY_OPTIONS,
    })),
  });
}

function invalidatePlatformQueries() {
  queryClient.invalidateQueries(
    { queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.PLATFORMS_LIST] },
  );
  queryClient.invalidateQueries(
    { queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.PLATFORM_ACTIONS_BY_CODE] },
  );
  queryClient.invalidateQueries(
    { queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.ROLE_CAPABILITIES] },
  );
  queryClient.invalidateQueries({ queryKey: [QueryServerKeys.USER.GET_USER_DATA] });
}

export function useUpdatePlatformActionMapping() {
  return useMutation({
    mutationFn: async ({
      code,
      actionName,
      enabled,
    }: {
      code: string;
      actionName: string;
      enabled: boolean;
    }) => {
      return await updatePlatformActionMapping(code, actionName, enabled);
    },
    mutationKey: [
        QueryServerKeys.SYSTEM_MANAGEMENT.UPDATE_PLATFORM_ACTION_MAPPING,
      ],
      onSuccess(result, { actionName }) {
        invalidatePlatformQueries();
        displaySuccessMessage(`${actionName} is successfully updated`);
      },
      onError(error: AxiosError) {
        const errorMessage = error && getErrorMessage(error);
        if (errorMessage) displayErrorMessage(errorMessage);
      },
  });
}

export function useUpdateRolePlatform() {
  return useMutation({
    mutationFn: async ({
      roleID,
      code,
      enabled,
    }: {
      roleID: number;
      code: string;
      enabled: boolean;
    }) => {
      return await updateRolePlatform(roleID, code, enabled);
    },
    mutationKey: [
        QueryServerKeys.SYSTEM_MANAGEMENT.UPDATE_ROLE_PLATFORM,
      ],
      onSuccess() {
        invalidatePlatformQueries();
        displaySuccessMessage('Role platform updated successfully');
      },
      onError(error: AxiosError) {
        const errorMessage = error && getErrorMessage(error);
        if (errorMessage) displayErrorMessage(errorMessage);
      },
  });
}

export function useUpdateRoleAction() {
  return useMutation({
    mutationFn: async ({
      roleID,
      actionName,
      enabled,
    }: {
      roleID: number;
      actionName: string;
      enabled: boolean;
    }) => {
      return await updateRoleAction(roleID, actionName, enabled);
    },
    mutationKey: [
        QueryServerKeys.SYSTEM_MANAGEMENT.UPDATE_ROLE_ACTION,
      ],
      onSuccess() {
        invalidatePlatformQueries();
        displaySuccessMessage('Role action updated successfully');
      },
      onError(error: AxiosError) {
        const errorMessage = error && getErrorMessage(error);
        if (errorMessage) displayErrorMessage(errorMessage);
      },
  });
}
